import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Trash2, Search as SearchIcon, History as HistoryIcon } from "lucide-react";

export default function ProfilePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { navigate("/login", { state: { from: "/profile" } }); return; }
    api.get("/history").then(({ data }) => setHistory(data)).finally(() => setLoadingHist(false));
  }, [user, loading, navigate]);

  const remove = async (id) => {
    await api.delete(`/history/${id}`);
    setHistory((h) => h.filter((x) => x.id !== id));
  };
  const clearAll = async () => {
    if (!window.confirm("Effacer tout l'historique ?")) return;
    await api.delete("/history");
    setHistory([]);
  };

  if (loading || !user || user === false) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-start gap-5 mb-12" data-testid="profile-header">
          <div className="w-20 h-20 rounded-full bg-[var(--omni-accent)] text-white flex items-center justify-center text-3xl font-black">
            {user.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight">{user.name}</h1>
            <p className="text-gray-600">{user.email}</p>
            <p className="text-xs text-gray-400 mt-1">Rôle : {user.role}</p>
          </div>
        </div>

        <section id="history">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2"><HistoryIcon size={18} /> Historique de recherche</h2>
            {history.length > 0 && (
              <Button variant="ghost" onClick={clearAll} data-testid="clear-history-btn" className="text-red-600 hover:text-red-700">
                <Trash2 size={16} className="mr-1" /> Tout effacer
              </Button>
            )}
          </div>
          {loadingHist && <div className="text-gray-500 text-sm">Chargement…</div>}
          {!loadingHist && history.length === 0 && (
            <div className="border border-dashed border-[var(--omni-border)] rounded-lg p-12 text-center text-gray-500">
              <p className="mb-3">Aucune recherche pour le moment.</p>
              <Button asChild className="bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white rounded-full">
                <Link to="/" data-testid="start-searching-btn">Commencer à chercher</Link>
              </Button>
            </div>
          )}
          <ul className="divide-y divide-[var(--omni-border)] border border-[var(--omni-border)] rounded-lg overflow-hidden">
            {history.map((h) => (
              <li key={h.id} className="flex items-center justify-between px-4 py-3 hover:bg-[var(--omni-surface)]" data-testid={`history-item-${h.id}`}>
                <Link to={`/search?q=${encodeURIComponent(h.query)}&type=${h.type}`} className="flex items-center gap-3 flex-1 min-w-0">
                  <SearchIcon size={15} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">{h.query}</span>
                  <span className="text-xs uppercase font-mono text-gray-400 ml-2">{h.type}</span>
                </Link>
                <span className="text-xs text-gray-400 mx-4 hidden sm:inline">{new Date(h.created_at).toLocaleString()}</span>
                <button onClick={() => remove(h.id)} className="text-gray-400 hover:text-red-600" data-testid={`history-delete-${h.id}`}>
                  <Trash2 size={15} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <Footer />
    </div>
  );
}
